import React from "react";
import { motion } from "framer-motion";
import { FileText, MessageSquare, Video, Mic, Sparkles, Award, ArrowRight } from "lucide-react";
import { HOME } from "@/constants/testIds";

const floatCards = [
  { icon: FileText, label: "ATS Resume", sub: "Optimised · 92%", pos: "top-4 -left-4 sm:-left-10", delay: 0 },
  { icon: MessageSquare, label: "Interview Ready", sub: "Confidence · High", pos: "top-24 -right-4 sm:-right-10", delay: 0.15 },
  { icon: Mic, label: "Communication", sub: "Fluency · A2 → B2", pos: "bottom-24 -left-4 sm:-left-14", delay: 0.3 },
  { icon: Video, label: "Mock Interview", sub: "Score · 8.6 / 10", pos: "bottom-4 right-2 sm:-right-6", delay: 0.45 },
];

export default function Hero({ site }) {
  const s = site || {};
  return (
    <section id="home" className="relative overflow-hidden bg-[#0B132B] text-white pt-32 pb-24 sm:pt-40 sm:pb-32">
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#3A506B]/25 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-[#1C2541]/40 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ring-white/15 bg-white/5 text-[11px] tracking-widest uppercase text-slate-300">
            <Sparkles size={12} className="text-slate-300" /> Career-Readiness Platform · Jodhpur
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
                     className="mt-5 font-display text-4xl sm:text-6xl leading-[1.05] font-medium">
            {s.hero_headline || "Become Job-Ready, Not Just Degree-Ready."}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                    className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
            {s.hero_sub}
          </motion.p>
          <div className="mt-4 text-xs text-slate-400 tracking-wide">{s.hero_tags}</div>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#contact" data-testid={HOME.heroCtaPrimary}
               className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#0B132B] text-sm font-semibold hover:-translate-y-0.5 transition-transform shadow-glow">
              {s.cta_primary || "Book Free Career Counselling"}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
            </a>
            <a href="#programs" data-testid={HOME.heroCtaSecondary}
               className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full ring-1 ring-white/20 hover:bg-white/5 text-sm font-medium transition-colors">
              {s.cta_secondary || "Explore Our Programs"}
            </a>
            <a href={`https://wa.me/${s.whatsapp || "918824207033"}`} target="_blank" rel="noreferrer" data-testid={HOME.heroCtaWhatsapp}
               className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full ring-1 ring-white/10 bg-white/5 hover:bg-white/10 text-sm font-medium transition-colors">
              {s.cta_whatsapp || "Talk to Us on WhatsApp"}
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 relative h-[480px] sm:h-[540px] hidden sm:block">
          <motion.div className="absolute inset-0" animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: "linear" }}>
            <div className="absolute inset-6 rounded-full ring-1 ring-white/10" />
            <div className="absolute inset-16 rounded-full ring-1 ring-white/5" />
            <div className="absolute inset-28 rounded-full ring-1 ring-white/5" />
          </motion.div>

          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
                      className="absolute inset-0 grid place-items-center">
            <div className="relative w-56 h-72 rounded-2xl glass-dark overflow-hidden shadow-high">
              <div className="absolute inset-0 shimmer" />
              {s.logo_url && (
                <img src={s.logo_url} alt="Be Hire Ready — Career readiness platform Jodhpur" className="absolute inset-0 m-auto w-32 h-32 object-cover rounded-xl opacity-90" loading="eager" decoding="async" />
              )}
              <div className="absolute bottom-3 left-3 right-3 text-[10px] uppercase tracking-widest text-slate-400 flex justify-between">
                <span>Candidate ID</span><span>BHR-2026</span>
              </div>
            </div>
          </motion.div>

          {floatCards.map(({ icon: Icon, label, sub, pos, delay }) => (
            <motion.div key={label}
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 + delay }}
                        className={`absolute ${pos} animate-float-slow`} style={{ animationDelay: `${delay}s` }}>
              <div className="glass-dark rounded-2xl px-4 py-3 flex items-center gap-3 shadow-elev">
                <div className="h-9 w-9 rounded-xl bg-white/10 grid place-items-center">
                  <Icon size={16} className="text-slate-200" />
                </div>
                <div>
                  <div className="text-[13px] font-medium text-white">{label}</div>
                  <div className="text-[10.5px] text-slate-400 tracking-wide">{sub}</div>
                </div>
              </div>
            </motion.div>
          ))}

          <motion.div className="absolute top-1/2 left-6 w-3 h-3 rounded-full bg-white/60"
                      animate={{ y: [0, -14, 0] }} transition={{ duration: 4, repeat: Infinity }} />
          <motion.div className="absolute top-10 right-24 w-2 h-2 rounded-full bg-white/40"
                      animate={{ y: [0, 10, 0] }} transition={{ duration: 5, repeat: Infinity }} />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 glass-dark px-4 py-2 rounded-full">
            <Award size={14} className="text-slate-300" />
            <span className="text-[11px] tracking-widest uppercase text-slate-300">Placement Ready</span>
          </div>
        </div>
      </div>
    </section>
  );
}
